import React, {useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {
	ACTION_BUTTON_SIZE,
	ActionButton,
	ButtonDropdown,
	COUNT_BADGE_PLACEMENT,
	CountBadge,
	DropdownList,
	EMPHASIS,
	FlexRow,
	Icon,
} from '@forecast-it/design-system';
import {AGENTIC_WORKERS_MESSAGE} from './agenticWorkersMessages';
import type {AgentDeclaration, AgentPage} from './agentDeclaration';
import {ensureAgentsRegistered, getAgentsForPage} from './agentRegistry';
import {useAgentPageState, useSummaryToast} from './agentRunState';
import {canSeeAgent} from './agentVisibility';
import {ScheduleDrawer} from '../scheduling/ScheduleDrawer';

ensureAgentsRegistered();

const MENU_OFFSET_PIXELS = 4;

export const agentControlDataCy = (agentId: string): string => `agent-button-${agentId}`;

// Positioned so the waiting badge can sit on the button's top-right corner.
const AgentControl = styled(FlexRow)`
	position: relative;
`;

const MenuItemRow = ({children}: {children: React.ReactNode}): JSX.Element => (
	<FlexRow alignItems={'center'} gap={'s'}>
		{children}
	</FlexRow>
);

type AgentButtonProps = {
	declaration: AgentDeclaration;
	isScheduleOpen: boolean;
	onOpenSchedule: () => void;
	onCloseSchedule: () => void;
};

// One agent's button and menu. A component of its own because each agent holds its own run state. Its
// schedule drawer's open state is owned by the toolbar, so only one agent's drawer is open at a time.
const AgentButton = ({declaration, isScheduleOpen, onOpenSchedule, onCloseSchedule}: AgentButtonProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const {isRunning, waitingCount, startRun, stopRun} = useAgentPageState(declaration);
	useSummaryToast(declaration);
	const [isMenuOpen, setMenuOpen] = useState(false);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const focusTriggerWhenIdleRef = useRef(false);

	const agentName = formatMessage(declaration.name);
	const waitingAccessibleName = formatMessage(
		{id: AGENTIC_WORKERS_MESSAGE.BADGE_WAITING},
		{
			count: waitingCount,
			resultSingular: formatMessage(declaration.resultNoun.singular),
			resultPlural: formatMessage(declaration.resultNoun.plural),
		}
	);

	const runFromMenu = (): void => {
		setMenuOpen(false);
		startRun();
	};

	const openSchedule = (): void => {
		setMenuOpen(false);
		onOpenSchedule();
	};

	// The Past Runs drawer is not built yet, so choosing it only closes the menu.
	const closeMenu = (): void => {
		setMenuOpen(false);
	};

	// The stop part disappears and the trigger becomes a different element once the run stops, so focus
	// is handed to the new trigger after the re-render instead of falling back to the page.
	useEffect(() => {
		const shouldFocusTrigger = !isRunning && focusTriggerWhenIdleRef.current;
		focusTriggerWhenIdleRef.current = false;
		if (shouldFocusTrigger) {
			triggerRef.current?.focus();
		}
	}, [isRunning]);

	const stopFromButton = (): void => {
		setMenuOpen(false);
		focusTriggerWhenIdleRef.current = true;
		stopRun();
	};

	return (
		<AgentControl flex={'0 0 auto'} data-cy={agentControlDataCy(declaration.id)}>
			<DropdownList open={isMenuOpen} onOpenChange={setMenuOpen}>
				<DropdownList.Trigger>
					{isRunning ? (
						<ActionButton
							ref={triggerRef}
							size={ACTION_BUTTON_SIZE.STANDARD}
							emphasis={EMPHASIS.MEDIUM}
							waiting={true}
							aria-label={formatMessage({id: AGENTIC_WORKERS_MESSAGE.BUTTON_WORKING_NAME}, {name: agentName})}
							trailingAction={{
								icon: 'stop',
								'aria-label': formatMessage({id: AGENTIC_WORKERS_MESSAGE.BUTTON_STOP}, {name: agentName}),
								onClick: stopFromButton,
								'data-cy': 'agent-button-stop',
							}}
							data-cy={'agent-button-trigger'}
						>
							{formatMessage({id: AGENTIC_WORKERS_MESSAGE.BUTTON_WORKING})}
						</ActionButton>
					) : (
						<ButtonDropdown
							ref={triggerRef}
							emphasis={EMPHASIS.MEDIUM}
							icon={'sparkle'}
							data-cy={'agent-button-trigger'}
						>
							{agentName}
						</ButtonDropdown>
					)}
				</DropdownList.Trigger>
				<DropdownList.Content align={'start'} side={'bottom'} offset={MENU_OFFSET_PIXELS} portalled={true}>
					<DropdownList.Item disabled={isRunning} onClick={runFromMenu} data-cy={'agent-menu-run'}>
						<MenuItemRow>
							<Icon icon={'sparkle'} size={'s'} newColor={isRunning ? 'disabled' : undefined} />
							{isRunning
								? formatMessage({id: AGENTIC_WORKERS_MESSAGE.MENU_RUNNING})
								: formatMessage({id: AGENTIC_WORKERS_MESSAGE.MENU_RUN}, {name: agentName})}
						</MenuItemRow>
					</DropdownList.Item>
					<DropdownList.Item onClick={openSchedule} data-cy={'agent-menu-schedule'}>
						<MenuItemRow>
							<Icon icon={'clock'} size={'s'} />
							{formatMessage({id: AGENTIC_WORKERS_MESSAGE.MENU_SCHEDULE}, {name: agentName})}
						</MenuItemRow>
					</DropdownList.Item>
					<DropdownList.Item onClick={closeMenu} data-cy={'agent-menu-past-runs'}>
						<MenuItemRow>
							<Icon icon={'list'} size={'s'} />
							{formatMessage({id: AGENTIC_WORKERS_MESSAGE.MENU_PAST_RUNS}, {name: agentName})}
							<CountBadge
								count={waitingCount}
								aria-label={waitingAccessibleName}
								placement={COUNT_BADGE_PLACEMENT.INLINE}
							/>
						</MenuItemRow>
					</DropdownList.Item>
				</DropdownList.Content>
			</DropdownList>
			<CountBadge count={waitingCount} aria-label={waitingAccessibleName} placement={COUNT_BADGE_PLACEMENT.CORNER} />
			<ScheduleDrawer
				isOpen={isScheduleOpen}
				onClose={onCloseSchedule}
				name={agentName}
				consumer={declaration.schedulingTarget}
			/>
		</AgentControl>
	);
};

type AgentToolbarButtonsProps = {
	page: AgentPage;
};

export const AgentToolbarButtons = ({page}: AgentToolbarButtonsProps): JSX.Element | null => {
	// The agent whose schedule drawer is open. Opening another agent's replaces it.
	const [scheduleAgentId, setScheduleAgentId] = useState<string | null>(null);
	const visibleAgents = getAgentsForPage(page).filter(canSeeAgent);
	if (visibleAgents.length === 0) {
		return null;
	}
	return (
		<FlexRow alignItems={'center'} gap={'s'} flex={'0 0 auto'} data-cy={'agent-toolbar-buttons'}>
			{visibleAgents.map(declaration => (
				<AgentButton
					key={declaration.id}
					declaration={declaration}
					isScheduleOpen={scheduleAgentId === declaration.id}
					onOpenSchedule={() => setScheduleAgentId(declaration.id)}
					onCloseSchedule={() => setScheduleAgentId(current => (current === declaration.id ? null : current))}
				/>
			))}
		</FlexRow>
	);
};
